import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthProvider';
import { BadgeCheck, Loader2 } from 'lucide-react';

export const TrialActivation: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const activateTrial = async () => {
      // Check if user selected a plan
      const selectedPlan = sessionStorage.getItem('selectedPlan');
      const selectedBillingPeriod = sessionStorage.getItem('selectedBillingPeriod');
      
      if (!selectedPlan || !session) {
        return;
      }

      // Clear the session storage
      sessionStorage.removeItem('selectedPlan');
      sessionStorage.removeItem('selectedBillingPeriod');

      setIsActivating(true);
      setError(null);

      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/billing/start-trial`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          body: JSON.stringify({
            plan: selectedPlan
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to start trial');
        }

        // Redirect to dashboard with success message
        navigate('/qr/dash?trial=started');
      } catch (err: any) {
        console.error('Trial activation error:', err);
        setError(err.message);
        setIsActivating(false);
      }
    };

    activateTrial();
  }, [session, navigate]);

  if (isActivating) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex flex-col items-center text-center">
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin mb-4" />
            <h3 className="text-xl font-semibold mb-2">Aktiviere deine kostenlose Testphase</h3>
            <p className="text-gray-600">Einen Moment bitte...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex flex-col items-center text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-red-600 text-2xl">!</span>
            </div>
            <h3 className="text-xl font-semibold mb-2">Fehler bei der Aktivierung</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/qr/dash')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Zum Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default TrialActivation; 